import './Title.scss';

import { cva, VariantProps } from 'class-variance-authority';
import classNames from 'classnames';
import { FC, ReactNode } from 'react';

import { Responsive } from '../../interfaces/Responsive';
import { splitDataAttributes } from '../../utils/dataAttributes';

const baseClassName = 'ds-Title';

type Level = 1 | 2 | 3 | 4 | 5;

const colorsVariance = cva(baseClassName, {
  variants: {
    color: {
      white: `${baseClassName}--color-white`,
    },
  },
});

const getLevelClassName = (level: TitleProps['level']): string => {
  if (typeof level === 'number') {
    return `${baseClassName}--level-${level}`;
  }

  const classNames: string[] = [
    `${baseClassName}--level-${level?.sm ?? 1}-sm`,
    `${baseClassName}--level-${level?.md ?? 1}-md`,
    `${baseClassName}--level-${level?.lg ?? 1}-lg`,
  ];

  return classNames.join(' ');
};

type TitleProps = {
  level?: Level | Responsive<Level>;
  className?: string;
  children?: ReactNode;
  tag?: 1 | 2 | 3 | 4 | 5 | 6;
} & React.HTMLAttributes<HTMLHeadingElement> &
  VariantProps<typeof colorsVariance>;

export const Title: FC<TitleProps> = ({
  level = 1,
  tag = 1,
  className,
  children = null,
  color = undefined,
  ...rest
}) => {
  const Tag = `h${tag}` as keyof JSX.IntrinsicElements;
  const { dataAttributes, ...otherProps } = splitDataAttributes(rest);

  return (
    <Tag
      className={classNames(
        [baseClassName, className],
        getLevelClassName(level),
        colorsVariance({ color }),
      )}
      {...dataAttributes}
      {...otherProps}
    >
      {children}
    </Tag>
  );
};

export default Title;
